import { Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TranscricaoItemDetalhe } from '../../../../../interfaces/entities/Transcricao/TranscricaoRevisao';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import {
  AtualizaItemTranscricao,
  atualizarTipoAtendimentoTranscricao,
  atualizarTranscricao,
} from '../../../../../services/TranscricaoBeneficiarioService';
import LoadingApp from '../../../../../components/LoadingApp';
import ModalFeedBack from '../../../../../components/ModalFeedBack';
import EditarProcedimentoModal, {
  EditarProcedimentoFormValues,
} from './EditarProcedimentoModal';
import EditarProfissionalModal, {
  EditarProfissionalFormValues,
} from './EditarProfissionalModal';
import RevisaoSectionTable from '../../components/TranscricaoRevisao';
import TranscricaoEmAndamentoCard from './TranscricaoEmAndamentoCard';
import { useTranscricaoRevisao } from '../hooks/useTranscricaoRevisao';
import { useTranscricao } from '../context';
import TipoAtendimentoRevisaoSection from './TipoAtendimentoRevisaoSection';
import {
  COLUNAS_BENEFICIARIO,
  COLUNAS_CID,
  COLUNAS_PROCEDIMENTOS_EDITAVEL,
  COLUNAS_PROFISSIONAL_EDITAVEL,
  mapBeneficiarioLinhas,
  mapCidLinhasDetalhe,
  mapProcedimentosParaLinhas,
  mapProfissionalLinhas,
  parseQuantidade,
} from '../../utils/transcricaoDadosPedidoUtils';
import { buildAtualizarTranscricaoPayload } from '../utils/novaTranscricaoPayloadUtils';
import {
  isCodTipoTranscricaoPreenchido,
  isCodTipoTranscricaoValido,
  normalizeCodTipoTranscricao,
} from '../../utils/tipoAtendimentoUtils';

const StepTranscricao: React.FC = () => {
  const accessibility = useAccessibility();
  const { dispatch } = useTranscricao();
  const [showModalFeedBack, setShowModalFeedBack] = useState(false);
  const [titleModal, setTitleModal] = useState('');
  const [subTitleModal, setSubTituloModal] = useState('');
  const [procedimentoEmEdicao, setProcedimentoEmEdicao] = useState<TranscricaoItemDetalhe | null>(
    null,
  );
  const [editandoProfissional, setEditandoProfissional] = useState(false);
  const [salvandoItem, setSalvandoItem] = useState(false);
  const [exibirTipoAtendimento, setExibirTipoAtendimento] = useState(false);
  const tipoAtendimentoVisibilidadeInicializadaRef = useRef(false);

  const handleError = useCallback((title: string, subTitle: string) => {
    setTitleModal(title);
    setSubTituloModal(subTitle);
    setShowModalFeedBack(true);
  }, []);

  const { revisaoStatus, procedimentos, detalheTranscricao } = useTranscricaoRevisao({
    onError: handleError,
  });

  const exibirEmAndamento =
    revisaoStatus === 'carregando' || revisaoStatus === 'em_andamento';

  const exibirRevisao = revisaoStatus === 'pronta';

  useEffect(() => {
    if (
      revisaoStatus !== 'pronta'
      || !detalheTranscricao
      || tipoAtendimentoVisibilidadeInicializadaRef.current
    ) {
      return;
    }

    tipoAtendimentoVisibilidadeInicializadaRef.current = true;
    setExibirTipoAtendimento(
      !isCodTipoTranscricaoPreenchido(detalheTranscricao.codTipoTranscricao),
    );
  }, [detalheTranscricao, revisaoStatus]);

  const handleEditarProfissional = useCallback(
    (linhaId: string | number) => {
      if (linhaId === 'profissional' && detalheTranscricao) {
        setEditandoProfissional(true);
      }
    },
    [detalheTranscricao],
  );

  const handleSalvarProfissional = useCallback(
    async (values: EditarProfissionalFormValues) => {
      if (!detalheTranscricao) return;

      setSalvandoItem(true);

      try {
        const payload = buildAtualizarTranscricaoPayload(detalheTranscricao, values);
        if (!payload) {
          handleError(
            'Erro ao salvar profissional',
            detalheTranscricao.idTranscricao
              ? 'Informe a UF do conselho profissional para continuar.'
              : 'Não foi possível identificar a transcrição. Tente novamente em instantes.',
          );
          return;
        }

        const resultado = await atualizarTranscricao(payload);
        if (!resultado.ok) {
          handleError('Erro ao salvar profissional', resultado.message);
          return;
        }

        dispatch({
          type: 'SET_DETALHE_TRANSCRICAO',
          payload: {
            ...detalheTranscricao,
            nomeSolicitanteInformado: values.profissional,
            numeroConselhoInformado: values.registro,
            siglaUfConselhoInformado: values.siglaUfConselhoInformado,
            idUfConselhoInformado: payload.idUfConselhoInformado,
            codigoConselhoInformado: values.codigoConselho,
          },
        });
      } finally {
        setSalvandoItem(false);
      }
    },
    [detalheTranscricao, dispatch, handleError],
  );

  const handleEditarProcedimento = useCallback(
    (linhaId: string | number) => {
      const item = procedimentos.find((procedimento) => procedimento.idTranscricaoItem === linhaId);
      if (item) {
        setProcedimentoEmEdicao(item);
      }
    },
    [procedimentos],
  );

  const handleAlterarTipoAtendimento = useCallback(
    async (codigo: string) => {
      if (!detalheTranscricao?.idTranscricao || !isCodTipoTranscricaoValido(codigo)) return;

      const codigoAtual = normalizeCodTipoTranscricao(detalheTranscricao.codTipoTranscricao);
      if (codigoAtual === codigo) return;

      setSalvandoItem(true);

      try {
        const resultado = await atualizarTipoAtendimentoTranscricao({
          idTranscricao: detalheTranscricao.idTranscricao,
          codTipoServicoTranscricao: codigo,
        });

        if (!resultado) {
          handleError(
            'Erro ao salvar tipo de atendimento',
            'Não foi possível atualizar o tipo de atendimento. Tente novamente em instantes.',
          );
          return;
        }

        dispatch({
          type: 'SET_DETALHE_TRANSCRICAO',
          payload: { ...detalheTranscricao, codTipoTranscricao: codigo },
        });
      } finally {
        setSalvandoItem(false);
      }
    },
    [detalheTranscricao, dispatch, handleError],
  );

  const handleSalvarProcedimento = useCallback(
    async (idTranscricaoItem: string, values: EditarProcedimentoFormValues) => {
      const procedimentoAtual = procedimentos.find(
        (item) => item.idTranscricaoItem === idTranscricaoItem,
      );

      if (!procedimentoAtual) return;

      if (!detalheTranscricao?.idTranscricao) {
        handleError(
          'Erro ao salvar procedimento',
          'Não foi possível identificar a transcrição. Tente novamente em instantes.',
        );
        return;
      }

      setSalvandoItem(true);

      try {
        const resultado = await AtualizaItemTranscricao({
          idTranscricao: detalheTranscricao.idTranscricao,
          idTranscricaoItem,
          tipoProdMed: values.tipoItem,
          codigo: values.codigoTuss,
          descricao: values.descricao,
          quantidade: parseQuantidade(values.quantidade),
        });

        if (!resultado.ok) {
          handleError('Erro ao salvar procedimento', resultado.message);
          return;
        }

        dispatch({
          type: 'UPDATE_PROCEDIMENTO',
          payload: {
            ...procedimentoAtual,
            tipoProdMed: values.tipoItem,
            codigo: values.codigoTuss,
            descricao: values.descricao,
            quantidade: parseQuantidade(values.quantidade),
          },
        });
      } finally {
        setSalvandoItem(false);
      }
    },
    [detalheTranscricao, dispatch, handleError, procedimentos],
  );

  return (
    <div
      className={`transcricao-step-content transcricao-step-content--revisao${exibirEmAndamento ? ' transcricao-step-content--revisao-em-andamento' : ''
        }${exibirRevisao ? ' transcricao-step-content--revisao-pronta' : ''}`}
    >
      {exibirEmAndamento && <TranscricaoEmAndamentoCard onError={handleError} />}

      {exibirRevisao && detalheTranscricao && (
        <div className="transcricao-revisao-sections">
          <RevisaoSectionTable
            titulo="Beneficiário"
            colunas={COLUNAS_BENEFICIARIO}
            linhas={mapBeneficiarioLinhas(detalheTranscricao)}
          />
          <RevisaoSectionTable
            titulo="Profissional"
            colunas={COLUNAS_PROFISSIONAL_EDITAVEL}
            linhas={mapProfissionalLinhas(detalheTranscricao)}
            showEditColumn
            onEditClick={handleEditarProfissional}
          />
          <RevisaoSectionTable
            titulo="CID — Emissão do pedido"
            colunas={COLUNAS_CID}
            linhas={mapCidLinhasDetalhe(detalheTranscricao)}
          />
          <RevisaoSectionTable
            titulo="Procedimentos"
            colunas={COLUNAS_PROCEDIMENTOS_EDITAVEL}
            linhas={mapProcedimentosParaLinhas(procedimentos)}
            showEditColumn
            onEditClick={handleEditarProcedimento}
          />
          {exibirTipoAtendimento && (
            <TipoAtendimentoRevisaoSection
              value={normalizeCodTipoTranscricao(detalheTranscricao.codTipoTranscricao)}
              onChange={handleAlterarTipoAtendimento}
              disabled={salvandoItem}
            />
          )}
        </div>
      )}

      {revisaoStatus === 'erro' && (
        <Typography style={accessibility.textStyleAccessibility}>
          Não foi possível carregar a revisão do pedido. Volte ao passo anterior e tente novamente.
        </Typography>
      )}

      <EditarProfissionalModal
        open={editandoProfissional}
        detalhe={detalheTranscricao}
        onClose={() => setEditandoProfissional(false)}
        onSave={handleSalvarProfissional}
      />

      <EditarProcedimentoModal
        open={!!procedimentoEmEdicao}
        procedimento={procedimentoEmEdicao}
        onClose={() => setProcedimentoEmEdicao(null)}
        onSave={handleSalvarProcedimento}
      />

      <ModalFeedBack
        showModal={showModalFeedBack}
        handleShowModal={() => setShowModalFeedBack(false)}
        icon="error"
        textButton="Fechar"
        title={titleModal}
        subTitle={subTitleModal}
        showButtonModal
        handleButtonModal={() => setShowModalFeedBack(false)}
        classNameModal="modal-feedback-error"
      />

      <LoadingApp showLoading={salvandoItem} text="Carregando..." />
    </div>
  );
};

export default StepTranscricao;
